import React, { useState } from 'react';
import { Button } from '../ui/button';
import { RefreshCw, Trash2, Loader2, Plus, X } from 'lucide-react';

const PageEditPanel = ({ page, pageIndex, onUpdate, onRegenerate, onDelete, regenerating, totalPages }) => {
  const [newField, setNewField] = useState('');

  const updateField = (key, value) => {
    onUpdate(pageIndex, { ...page, [key]: value });
  };

  const addField = () => {
    if (!newField.trim()) return;
    const updated = [...(page.fields || []), newField.trim()];
    updateField('fields', updated);
    setNewField('');
  };

  const removeField = (fi) => {
    const updated = (page.fields || []).filter((_, i) => i !== fi);
    updateField('fields', updated);
  };

  return (
    <div data-testid={`edit-panel-page-${pageIndex}`} className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-sans font-bold text-stone-800 text-sm">Edit Page {pageIndex + 1}</h4>
        <div className="flex items-center gap-2">
          <Button
            data-testid={`regenerate-page-${pageIndex}`}
            onClick={() => onRegenerate(pageIndex)}
            disabled={regenerating}
            variant="outline"
            className="h-8 px-3 text-xs font-sans font-semibold rounded-full border-primary/30 text-primary hover:bg-primary/5"
          >
            {regenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            AI Regenerer
          </Button>
          {totalPages > 1 && (
            <Button
              data-testid={`delete-page-${pageIndex}`}
              onClick={() => onDelete(pageIndex)}
              variant="ghost"
              className="h-8 w-8 p-0 text-stone-400 hover:text-red-500"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-sans font-medium text-stone-500 mb-1 block">Title</label>
        <input
          data-testid={`edit-page-title-${pageIndex}`}
          value={page.title || ''}
          onChange={(e) => updateField('title', e.target.value)}
          className="w-full text-sm font-sans font-semibold text-stone-800 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
        />
      </div>

      <div>
        <label className="text-xs font-sans font-medium text-stone-500 mb-1 block">Content</label>
        <textarea
          data-testid={`edit-page-content-${pageIndex}`}
          value={page.content || ''}
          onChange={(e) => updateField('content', e.target.value)}
          rows={3}
          className="w-full text-sm font-sans text-stone-600 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none resize-none"
        />
      </div>

      {page.fields && (
        <div>
          <label className="text-xs font-sans font-medium text-stone-500 mb-1.5 block">Fields</label>
          <div className="space-y-1.5">
            {page.fields.map((field, fi) => (
              <div key={fi} className="flex items-center gap-2">
                <input
                  value={field}
                  onChange={(e) => {
                    const updated = [...page.fields];
                    updated[fi] = e.target.value;
                    updateField('fields', updated);
                  }}
                  className="flex-1 text-xs font-sans text-stone-700 bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-1.5 focus:border-primary outline-none"
                />
                <button onClick={() => removeField(fi)} className="text-stone-400 hover:text-red-400 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <input
              data-testid={`add-field-input-${pageIndex}`}
              value={newField}
              onChange={(e) => setNewField(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addField()}
              placeholder="Add new field..."
              className="flex-1 text-xs font-sans text-stone-600 bg-stone-50 border border-dashed border-stone-300 rounded-lg px-2.5 py-1.5 focus:border-primary outline-none"
            />
            <button data-testid={`add-field-btn-${pageIndex}`} onClick={addField} className="text-primary hover:text-primary/80 transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageEditPanel;
